import { App, Button, Slider, Switch, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createVibrationFrames } from '@aitomol/anim';
import type { Editor, Structure } from '@aitomol/core';
import type { PlatformPlayback } from './playback';
import { VIBRATION_VECTORS, patchRepresentation, removeRepresentation, supportsRepresentation } from './representations';
import { fetchVibrationMode, modeRms } from './vibration';
import type { ModeVector, VibrationModeSet } from './types';

/**
 * Normal-mode panel: the replacement for JSmol's
 * `load DATA "vibration"` / `vibration ON` / `DRAW arrow` script sequence.
 *
 * Two things the script channel could not do are added here: displacement arrows
 * drawn by the toolkit's own representation, and physical units for both the
 * arrow length and the animation amplitude.
 */
export interface VibrationPanelProps {
    editor: Editor;
    playback: PlatformPlayback;
    path?: string;
    /** Active document; a change stops the animation and resets the level. */
    structure: Structure | null;
    /** Modes parsed straight out of the loaded file (Gaussian logs). */
    fileModes: VibrationModeSet | null;
    /** Mode count reported by `/jsmol/options`. */
    apiLevels: number;
}

const ANIMATION_STEPS = 48;
const DEFAULT_ARROW_LENGTH = 0.8;
const DEFAULT_AMPLITUDE = 0.25;

export const VibrationPanel: React.FC<VibrationPanelProps> = ({ editor, playback, path, structure, fileModes, apiLevels }) => {
    const { message } = App.useApp();
    const { t } = useTranslation('common');

    const levelCount = Math.max(apiLevels, fileModes?.modes.length ?? 0);
    const [level, setLevel] = useState(1);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<ModeVector | null>(null);
    const [origin, setOrigin] = useState<'file' | 'api' | null>(null);
    const [showArrows, setShowArrows] = useState(true);
    const [arrowLength, setArrowLength] = useState(DEFAULT_ARROW_LENGTH);
    const [amplitude, setAmplitude] = useState(DEFAULT_AMPLITUDE);
    const [fps, setFps] = useState(12);
    const [playing, setPlaying] = useState(false);
    const requestToken = useRef(0);
    const modeRef = useRef<ModeVector | null>(null);
    modeRef.current = mode;
    const supported = supportsRepresentation(editor, VIBRATION_VECTORS);

    const frequency = fileModes?.frequencies[level - 1] ?? null;
    const intensity = fileModes?.intensities[level - 1] ?? null;

    /**
     * Draws (or clears) the arrows. A mode that came from the file is referenced
     * by index so the toolkit reads it from the structure; a mode fetched from
     * the server has to be handed over explicitly.
     */
    const pushArrows = useCallback(
        (vectors: ModeVector | null, fromFile: boolean, levelIndex: number, length: number) => {
            if (!supported) return;
            if (!vectors) {
                void removeRepresentation(editor, VIBRATION_VECTORS);
                return;
            }
            void patchRepresentation(
                editor,
                VIBRATION_VECTORS,
                fromFile ? { vectors: undefined, mode: levelIndex, length } : { vectors, length },
                { colorBy: 'magnitude', radius: 0.06 }
            );
        },
        [editor, supported]
    );

    const stopAnimation = useCallback(() => {
        playback.stopGenerated();
        setPlaying(false);
    }, [playback]);

    const startAnimation = useCallback(
        (vectors: ModeVector | null, nextAmplitude: number) => {
            if (!vectors) return;
            const reference = editor.model?.atoms.coordinates();
            if (!reference || reference.length === 0) {
                message.warning(t('aitomol.no_structure'));
                return;
            }
            playback.startGenerated(
                createVibrationFrames(reference, [vectors], { steps: ANIMATION_STEPS, amplitude: nextAmplitude, relative: true })
            );
            setPlaying(true);
        },
        [editor, message, playback, t]
    );

    const loadLevel = useCallback(
        async (nextLevel: number): Promise<{ vectors: ModeVector | null; fromFile: boolean }> => {
            const token = ++requestToken.current;
            const fromFile = fileModes?.modes[nextLevel - 1];
            if (fromFile) {
                setOrigin('file');
                return { vectors: fromFile, fromFile: true };
            }
            if (!path || apiLevels <= 0) {
                setOrigin(null);
                return { vectors: null, fromFile: false };
            }
            setLoading(true);
            try {
                const reference = editor.model?.atoms.coordinates();
                if (!reference) return { vectors: null, fromFile: false };
                const result = await fetchVibrationMode(path, nextLevel, reference);
                if (token !== requestToken.current) return { vectors: null, fromFile: false };
                if (!result.mode) {
                    message.error(result.error ?? t('aitomol.vibration_failed'));
                    setOrigin(null);
                    return { vectors: null, fromFile: false };
                }
                setOrigin('api');
                return { vectors: result.mode, fromFile: false };
            } finally {
                if (token === requestToken.current) setLoading(false);
            }
        },
        [apiLevels, editor, fileModes, message, path, t]
    );

    // Load the selected mode whenever the document or the level changes.
    useEffect(() => {
        if (levelCount === 0) {
            setMode(null);
            pushArrows(null, false, 1, arrowLength);
            stopAnimation();
            return;
        }
        let cancelled = false;
        void (async () => {
            const loaded = await loadLevel(level);
            if (cancelled) return;
            setMode(loaded.vectors);
            pushArrows(showArrows ? loaded.vectors : null, loaded.fromFile, level, arrowLength);
        })();
        return () => {
            cancelled = true;
        };
        // arrowLength and showArrows are handled by their own effects below;
        // including them here would refetch on every slider tick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level, levelCount, loadLevel]);

    // A different document invalidates the animation and the level choice.
    useEffect(() => {
        stopAnimation();
        setLevel(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structure]);

    const onLevelChange = (delta: number): void => {
        setLevel((current) => Math.min(levelCount, Math.max(1, current + delta)));
    };

    const onTogglePlay = (): void => {
        if (playing) {
            stopAnimation();
            return;
        }
        if (levelCount === 0) {
            message.warning(t('aitomol.no_vibration'));
            return;
        }
        const vectors = modeRef.current;
        if (vectors) {
            startAnimation(vectors, amplitude);
            return;
        }
        void (async () => {
            const loaded = await loadLevel(level);
            if (!loaded.vectors) return;
            setMode(loaded.vectors);
            pushArrows(showArrows ? loaded.vectors : null, loaded.fromFile, level, arrowLength);
            startAnimation(loaded.vectors, amplitude);
        })();
    };

    const onAmplitudeChange = (value: number): void => {
        setAmplitude(value);
        if (playing && modeRef.current) startAnimation(modeRef.current, value);
    };

    const onArrowLengthChange = (value: number): void => {
        setArrowLength(value);
        if (!showArrows) return;
        pushArrows(modeRef.current, origin === 'file', level, value);
    };

    const onToggleArrows = (checked: boolean): void => {
        setShowArrows(checked);
        pushArrows(checked ? modeRef.current : null, origin === 'file', level, arrowLength);
    };

    const onFpsChange = (value: number): void => {
        setFps(value);
        playback.setFps(value);
    };

    const rms = useMemo(() => (mode ? modeRms(mode) : 0), [mode]);

    if (levelCount === 0) return null;

    return (
        <div className="aitomol-panel aitomol-panel-wide">
            <div className="aitomol-panel-title">
                <span>{t('aitomol.vibration')}</span>
                <span className="aitomol-panel-meta">
                    {levelCount} {t('aitomol.modes')}
                    {origin ? ` · ${t(origin === 'file' ? 'aitomol.source_file' : 'aitomol.source_api')}` : ''}
                </span>
            </div>

            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.mode')}</span>
                <div className="aitomol-stepper-row">
                    <Button size="small" onClick={() => onLevelChange(-1)} disabled={level <= 1 || loading}>
                        &minus;
                    </Button>
                    <span className="aitomol-level">
                        {level}/{levelCount}
                    </span>
                    <Button size="small" onClick={() => onLevelChange(1)} disabled={level >= levelCount || loading}>
                        +
                    </Button>
                    <Tooltip title={playing ? t('aitomol.stop_vibration') : t('aitomol.play_vibration')}>
                        <Button size="small" type={playing ? 'primary' : 'default'} loading={loading} onClick={onTogglePlay}>
                            {playing ? t('aitomol.stop') : t('aitomol.play')}
                        </Button>
                    </Tooltip>
                </div>
            </div>

            {frequency !== null ? (
                <div className="aitomol-panel-foot">
                    <span className="aitomol-hint">
                        {t('aitomol.frequency')}: {frequency.toFixed(1)} cm&#8315;&#185;
                    </span>
                    {intensity !== null ? (
                        <span className="aitomol-hint">
                            {t('aitomol.ir_intensity')}: {intensity.toFixed(1)}
                        </span>
                    ) : null}
                </div>
            ) : null}

            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.amplitude')}</span>
                <Slider
                    className="aitomol-slider"
                    min={0.05}
                    max={1.5}
                    step={0.05}
                    value={amplitude}
                    tooltip={{ formatter: (value) => `${value?.toFixed(2)} A rms` }}
                    onChange={onAmplitudeChange}
                />
                <span className="aitomol-value">{amplitude.toFixed(2)}</span>
            </div>

            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.speed')}</span>
                <Slider className="aitomol-slider" min={2} max={30} step={1} value={fps} onChange={onFpsChange} />
                <span className="aitomol-value">{fps} fps</span>
            </div>

            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.vectors')}</span>
                <Switch size="small" checked={showArrows} disabled={!supported} onChange={onToggleArrows} />
                {showArrows && supported ? (
                    <>
                        <Slider
                            className="aitomol-slider"
                            min={0.1}
                            max={3}
                            step={0.05}
                            value={arrowLength}
                            tooltip={{ formatter: (value) => `${value?.toFixed(2)} A` }}
                            onChange={onArrowLengthChange}
                        />
                        <span className="aitomol-value">{arrowLength.toFixed(2)}</span>
                    </>
                ) : null}
            </div>

            {!supported ? <div className="aitomol-panel-foot aitomol-warn">{t('aitomol.vectors_unsupported')}</div> : null}
            {rms > 0 ? <div className="aitomol-panel-foot aitomol-hint">rms {rms.toExponential(2)} A (raw)</div> : null}
        </div>
    );
};